import { useEffect, useState } from "react";
import { UserStar, X } from "lucide-react";
import TabHeader from "./TabHeader";
import WorkHistoryTab from "./WorkHistoryTab";
import PassportDetailsTab from "./PassportDetailsTab";
import TravelHistoryTab from "./TravelHistoryTab";
import FormFooter from "./FormFooter";
import { authFetch } from "../../../api";
import ConfirmDetails from "./ConfirmDetails";

export default function ProfessionalInfoForm({ onClose }) {
  const userId = localStorage.getItem("userId");
  const [activeTab, setActiveTab] = useState("work");

  const [formData, setFormData] = useState({
    // Work
    hasWorked: "yes",
    organization: "",
    workingFrom: "",
    workingTill: "",
    currentlyWorking: false,
    jobIndustry: "",
    jobRole: "",
    jobType: "",
    income: "",

    // Passport
    hasPassport: "yes",
    passportFile: "",
    passportNumber: "",
    citizenOf: "",
    countryOfIssue: "",
    placeOfIssue: "",
    dateOfIssue: "",
    expiryDate: "",
    countryOfResidence: "",

    // Travel
    hasTravelled: "yes",
    hasAppliedVisa: "no",
    travelStatus: "",
    travelCountry: "",
    visaType: "",
    visaStatus: "",
    issueDate: "",
    expiryDate2: "",
    rejectionDate: "",
    rejectionReason: "",
    travelTentativeDate: "",
  });

  const [showAddForm, setShowAddForm] = useState(false);

  // Work states
  const [editingIndex, setEditingIndex] = useState(null);
  const [experiences, setExperiences] = useState([]);

  // Passport states
  const [editingPassport, setEditingPassport] = useState(null);
  const [passportData, setPassportData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Travel states (separate index/id from work editingIndex)
  const [travelHistoryList, setTravelHistoryList] = useState([]);
  const [editingTravelIndex, setEditingTravelIndex] = useState(null);
  const [editingTravelId, setEditingTravelId] = useState(null);
  const [passportError, setPassportError] = useState("");
  const [openReview, setOpenReview] = useState(false);
  const [viewMode, setViewMode] = useState("form");
  const [visaUploadStatus, setVisaUploadStatus] = useState("pending");
  const [passportUploadStatus, setPassportUploadStatus] = useState("pending");
  const [allWorkDeleted, setAllWorkDeleted] = useState(false);
  const [travelHistoryDeleted, setTravelHistoryDeleted] = useState(false);

  /** FETCH work history */
  const fetchWorkHistory = async () => {
    try {
      const response = await authFetch(
        "application/show-work-history-details-user",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ user_id: Number(userId) }),
        }
      );

      const data = await response.json();
      setExperiences(data.data || []);
    } catch (error) {
      console.error("Failed to fetch:", error);
    }
  };

  const fetchPassportData = async () => {
    try {
      const response = await authFetch(
        "application/show-passport-details-user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId }),
        }
      );

      const data = await response.json();

      setPassportData(data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  /** FETCH travel history (centralized) */
  const fetchTravelHistory = async () => {
    try {
      const response = await authFetch("application/show-all-travel-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: Number(userId) }),
      });
      const data = await response.json();
      // data.data expected to be array of travel entries
      setTravelHistoryList(data.data || []);
    } catch (err) {
      console.error("Failed to fetch travel history:", err);
      setTravelHistoryList([]);
    }
  };

  useEffect(() => {
    fetchWorkHistory();
    fetchPassportData();
    fetchTravelHistory();
  }, []);

  /** INPUT HANDLER (file + others) */
  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  /** Toggle currently working */
  const handleWorkStatusChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      currentlyWorking: checked,
      workingTill: checked ? "" : prev.workingTill,
    }));
  };

  /** Travel checkbox handler (generic) */
  const handleCheckboxChange = (value) => {
    setFormData((prev) => ({ ...prev, proofAvailable: value }));
  };

  /** Save Work History */
  const handleSubmitWorkHistory = async () => {
    try {
      const expId =
        editingIndex !== null ? experiences[editingIndex]?.id : null;

      const workPayload = {
        id: expId || undefined,
        user_id: Number(userId),
        name_of_org: formData.organization,
        curr_work: formData.currentlyWorking ? 1 : 0,
        working_from: formData.workingFrom
          ? formData.workingFrom + "-01"
          : null,
        working_to:
          formData.currentlyWorking || !formData.workingTill
            ? null
            : formData.workingTill + "-01",
        job_industry: Number(formData.jobIndustry),
        job_role: formData.jobRole,
        job_type: Number(formData.jobType),
        income: Number(formData.income),
      };

      let apiUrl;
      let finalPayload;

      if (editingIndex !== null) {
        apiUrl = "application/edit-work-history-details";
        finalPayload = workPayload;
      } else {
        apiUrl = "application/add-work-history-details";
        finalPayload = {
          user_id: Number(userId),
          data: [workPayload],
        };
      }

      const response = await authFetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(finalPayload),
      });

      const data = await response.json();
      if (data.success) {
        fetchWorkHistory();
        setShowAddForm(false);
        setEditingIndex(null);
      }
    } catch (error) {
      console.error("Submit error:", error);
    }
  };

  // Save Passport Details
  const handleSubmitPassport = async () => {
    try {
      // -------------------------
      // CASE 1 — NO PASSPORT
      // -------------------------
      if (formData.hasPassport === "no") {
        const payload = {
          user_id: Number(userId),
          has_passport: 0,
          tentative_date: formData.tentativeDate || null,
        };

        const response = await authFetch(
          "application/add-passport-details-user",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();
     

        // refresh passport list so footer Next button works
        await fetchPassportData();
        return;
      }

      // -------------------------
      // CASE 2 — EDIT PASSPORT
      // -------------------------
      if (editingPassport) {
        const payload = {
          id: editingPassport.id,
          user_id: Number(userId),
          passport_number: formData.passportNumber,
          citizen_of: Number(formData.citizenOf),
          country_of_issue: Number(formData.countryOfIssue),
          place_of_issue: formData.placeOfIssue,
          date_of_issue: formData.dateOfIssue,
          expiry_date: formData.expiryDate,
          country_of_residence: Number(formData.countryOfResidence),
        };

        const response = await authFetch(
          "application/edit-passport-details-user",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        const data = await response.json();
  

        setEditingPassport(null);
        setIsEditing(false);
        await fetchPassportData();
        return;
      }

      // -------------------------
      // CASE 3 — ADD NEW PASSPORT
      // -------------------------
      const payload = {
        user_id: Number(userId),
        passport_number: formData.passportNumber,
        citizen_of: formData.citizenOf ? Number(formData.citizenOf) : 1,
        country_of_issue: formData.countryOfIssue
          ? Number(formData.countryOfIssue)
          : 1,
        place_of_issue: formData.placeOfIssue,
        date_of_issue: formData.dateOfIssue,
        expiry_date: formData.expiryDate,
        country_of_residence: formData.countryOfResidence
          ? Number(formData.countryOfResidence)
          : 1,
      };
  
      const response = await authFetch(
        "application/add-passport-details-user",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      await fetchPassportData();
    } catch (error) {
      console.error("Passport Submit Error:", error);
    }
  };

  // ------------------------------
  // TRAVEL: Add / Edit / Delete
  // ------------------------------
  // Submit (Add or Edit)
  const handleSubmitTravel = async () => {
    try {
      // CASE A — User has NOT travelled
      if (formData.hasTravelled === "no") {
        // CASE 1 — Not travelled + Not applied for visa
        if (formData.hasAppliedVisa === "no") {
          await authFetch("application/add-travel-history", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id: Number(userId),
              travel_history: [
                {
                  visa_tentative_date: formData.travelTentativeDate,
                  has_travelled: formData.hasTravelled === "no" ? 0 : 1,
                  has_applied: formData.hasAppliedVisa === "no" ? 0 : 1,
                },
              ],
            }),
          });

          fetchTravelHistory();
          setShowAddForm(false);
          setEditingTravelIndex(null);
          setEditingTravelId(null);
          return;
        }

        // CASE 2 — Not travelled but has applied for visa
        await authFetch("application/add-travel-history", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: Number(userId),
            travel_history: [
              {
                has_travelled: formData.hasTravelled === "no" ? 0 : 1,
                has_applied: formData.hasAppliedVisa === "no" ? 0 : 1,
                types_of_visa: formData.visaType,
                visa_status: formData.visaStatus,
                date_of_issue: formData.issueDate || null,
                expiry_date: formData.expiryDate2 || null,
              },
            ], // Or visa attempt data if needed
          }),
        });

        fetchTravelHistory();
        setShowAddForm(false);
        return;
      }

      // ------- CASE B — User HAS travelled (Add/Edit same logic) -------
      const travelEntry = {
        travel_status: Number(formData.travelStatus),
        travel_country: formData.travelCountry,
        types_of_visa: formData.visaType,
        visa_status: formData.visaStatus,
        date_of_issue: formData.issueDate || null,
        expiry_date: formData.expiryDate2 || null,
        date_of_rejection:
          formData.visaStatus === "Rejected" ? formData.rejectionDate : null,
        reason_of_rejection:
          formData.visaStatus === "Rejected" ? formData.rejectionReason : null,
      };

      // EDIT
      if (editingTravelId) {
        const response = await authFetch("application/edit-travel-history", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: editingTravelId,
            user_id: Number(userId),
            ...travelEntry,
          }),
        });

        fetchTravelHistory();
        setShowAddForm(false);
        setEditingTravelId(null);
        setEditingTravelIndex(null);
        return;
      }

      // ADD
      const response = await authFetch("application/add-travel-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: Number(userId),
          travel_history: [travelEntry],
        }),
      });

      fetchTravelHistory();
      setShowAddForm(false);
      setEditingTravelId(null);
      setEditingTravelIndex(null);
    } catch (error) {
      console.error("Travel Submit Error:", error);
    }
  };

  // Edit travel entry: populate form and set editingTravelId
  const handleEditTravel = (index) => {
    const item = travelHistoryList[index];
    if (!item) return;

    setFormData((prev) => ({
      ...prev,
      hasTravelled: "yes",
      travelStatus: item.travel_status?.toString() || "",
      travelCountry: item.travel_country || "",
      visaType: item.types_of_visa?.toString() || "",
      visaStatus: item.visa_status?.toString() || "",
      issueDate: item.date_of_issue || "",
      expiryDate2: item.expiry_date || "",
      rejectionDate: item.date_of_rejection || "",
      rejectionReason: item.reason_of_rejection || "",
    }));

    setVisaUploadStatus("skipped"); // 🔥 CRITICAL
    setShowAddForm(true);
    setEditingTravelIndex(index);
    setEditingTravelId(item.id);
    setActiveTab("travel");
  };

  // ------------------------------
  // Work Edit/Delete (existing)
  // ------------------------------
  const handleEdit = (index) => {
    const exp = experiences[index];

    setFormData((prev) => ({
      ...prev,
      hasWorked: "yes",
      organization: exp.name_of_organization,
      workingFrom: exp.working_from?.slice(0, 7),
      workingTill: exp.working_to ? exp.working_to.slice(0, 7) : "",
      currentlyWorking: exp.currently_working === 1,
      jobIndustry: exp.job_industry,
      jobRole: exp.job_role,
      jobType: exp.job_type,
      income: exp.income,
    }));

    setEditingIndex(index);
    setShowAddForm(true);
    setActiveTab("work");
  };

  const handleDelete = async (index) => {
    const expId = experiences[index]?.id;
    const passportId = passportData?.[index]?.id;
    const travelHistoryId = travelHistoryList[index]?.id;

    try {
      const response = await authFetch(
        `${
          activeTab === "work"
            ? "application/delete-work-history-details"
            : activeTab === "passport"
            ? "application/delete-passport-details-user"
            : "application/delete-travel-history"
        }`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            activeTab === "work"
              ? { id: expId }
              : activeTab === "passport"
              ? { id: passportId }
              : { id: travelHistoryId }
          ),
        }
      );

      const data = await response.json();

      if (data.success === true) {
        if (activeTab === "work") {
          setExperiences((prev) => {
            const updated = prev.filter((_, i) => i !== index);

            if (updated.length === 0) {
              setAllWorkDeleted(true);
            }

            return updated;
          });

          fetchWorkHistory();
        }

        if (activeTab === "travel") {
          setTravelHistoryList((prev) => {
            const updated = prev.filter((_, i) => i !== index);
            if (updated.length === 0) {
              setTravelHistoryDeleted(true);
            }
            return updated;
          });
        }

        await fetchTravelHistory();
        setEditingTravelIndex(null);
        setEditingTravelId(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  useEffect(() => {
    if (activeTab === "travel") {
      setVisaUploadStatus("pending");
    }
  }, [activeTab]);

  return (
    <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center p-4">
      <div
        className={`${
          viewMode === "review" ? "bg-[#FFFDF6]" : "bg-white"
        } rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden`}
      >
        {/* Header */}
        <div className="bg-[#572575] font-inter text-white px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserStar />
            <div className="text-[20px]">
              {viewMode === "review"
                ? "Confirm Your Details"
                : "Professional Information Collection"}
            </div>
          </div>

          <button onClick={onClose} className="hover:bg-purple-600 p-1 rounded">
            <X size={24} />
          </button>
        </div>

        {/* Tabs */}
        <TabHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          // disabled={viewMode === "review"}
        />

        <div
          className="overflow-y-auto p-8"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          {viewMode === "review" ? (
            <ConfirmDetails
              experiences={experiences}
              passportData={passportData}
              travelHistoryList={travelHistoryList}
              activeTab={activeTab}
              onBack={() => setViewMode("form")}
              //  CONNECT EDITS
              onEditWork={(index) => {
                setViewMode("form");
                handleEdit(index);
              }}
              onEditPassport={(passport) => {
                setViewMode("form");
                setActiveTab("passport");

                setEditingPassport(passport);
                setFormData((prev) => ({
                  ...prev,
                  hasPassport: "yes",
                  passportNumber: passport.passport_number,
                  citizenOf: passport.citizen_of,
                  countryOfIssue: passport.country_of_issue,
                  placeOfIssue: passport.place_of_issue,
                  dateOfIssue: passport.date_of_issue,
                  expiryDate: passport.expiry_date,
                  countryOfResidence: passport.country_of_residence,
                }));
              }}
              onEditTravel={(index) => {
                setViewMode("form");
                handleEditTravel(index);
              }}
            />
          ) : (
            <>
              {activeTab === "work" && (
                <WorkHistoryTab
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleWorkStatusChange={handleWorkStatusChange}
                  experiences={experiences}
                  setExperiences={setExperiences}
                  showAddForm={showAddForm}
                  setShowAddForm={setShowAddForm}
                  editingIndex={editingIndex}
                  setEditingIndex={setEditingIndex}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  userId={userId}
                  fetchWorkHistory={fetchWorkHistory}
                  allWorkDeleted={allWorkDeleted}
                />
              )}

              {activeTab === "passport" && (
                <PassportDetailsTab
                  formData={formData}
                  handleInputChange={handleInputChange}
                  setFormData={setFormData}
                  editingPassport={editingPassport}
                  setEditingPassport={setEditingPassport}
                  handleDelete={handleDelete}
                  passportError={passportError}
                  setPassportError={setPassportError}
                  passportUploadStatus={passportUploadStatus}
                  setPassportUploadStatus={setPassportUploadStatus}
                  setVisaUploadStatus={setVisaUploadStatus}
                  passportData={passportData}
                  setIsEditing={setIsEditing}
                  isEditing={isEditing}
                />
              )}

              {activeTab === "travel" && (
                <TravelHistoryTab
                  formData={formData}
                  setFormData={setFormData}
                  handleInputChange={handleInputChange}
                  travelHistoryList={travelHistoryList}
                  setTravelHistoryList={setTravelHistoryList}
                  handleEditTravel={handleEditTravel}
                  handleDelete={handleDelete}
                  showAddForm={showAddForm}
                  setShowAddForm={setShowAddForm}
                  visaUploadStatus={visaUploadStatus}
                  setVisaUploadStatus={setVisaUploadStatus}
                  travelHistoryDeleted={travelHistoryDeleted}
                />
              )}

              {/* {viewMode === "form" && (
                <div className="px-8 pt-4">
                  <button
                    type="button"
                    onClick={() => setViewMode("review")}
                    className="text-sm text-[#572575] underline"
                  >
                    Review
                  </button>
                </div>
              )} */}
            </>
          )}
        </div>

        {/* Footer */}
        {viewMode === "form" && (
          <FormFooter
            setFormData={setFormData}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            formData={formData}
            setExperiences={setExperiences}
            experiences={experiences}
            showAddForm={showAddForm}
            setShowAddForm={setShowAddForm}
            handleSubmitWorkHistory={handleSubmitWorkHistory}
            handleSubmitPassport={handleSubmitPassport}
            handleSubmitTravel={handleSubmitTravel}
            passportData={passportData}
            setPassportError={setPassportError}
            editingPassport={editingPassport}
            allWorkDeleted={allWorkDeleted}
            setViewMode={setViewMode}
          />
        )}
      </div>
    </div>
  );
}
